// Shared aggregation helpers for the uptime KPI cards. Both the
// regular page (`UptimeOverview`) and the wall-display dashboard
// (`UptimeDashboardKpis`) reuse these so the two surfaces draw the
// same shape from the same numbers.
//
// Functions accept structural minimal types so they work with both
// `UptimeTargetFragment` (full history) and `UptimeInlineFragment`
// (no history arrays). When history fields are absent the aggregators
// return null-filled arrays; headline numbers still display correctly.
//
// Mute semantics
// ==============
// Every helper here filters muted targets out at the very top of the
// loop. The fleet KPI strip is "what does the operator-relevant
// fleet look like right now?" -- a target the operator silenced
// shouldn't show up in any of these series, otherwise an obvious
// post-mute green dashboard would still flash red on a 30-day
// history bar. By doing the filter once inside each helper, callers
// can pass the raw targets list without remembering to pre-filter,
// and a future caller can't accidentally regress by forgetting to.

type DailyEntry = { Date: string; TotalChecks: number; DownChecks: number }
type HourlyEntry = { BucketStart: string; AvgDurationMs?: number | null }
type TargetWithHistory = {
  IsMuted: boolean
  DailyHistory?: DailyEntry[]
  LatencyHourlyHistory?: HourlyEntry[]
  DriftDetectedAt?: string | null
}

// Number of buckets in each sparkline. Uptime / incidents / drift
// use a 30-day window matched to DailyHistory; latency uses a
// 24-hour window matched to LatencyHourlyHistory. Both lengths are
// stable so cards line up edge-to-edge across the strip.
export const UPTIME_SPARK_DAYS = 30
export const UPTIME_SPARK_HOURS = 24

// Aggregate the per-target daily history into a single series across
// the whole fleet. We pool checks/down checks across targets per day
// (matches the SQL definition of UptimePercent24h etc) so the
// sparkline shows the same uptime curve operators see in the headline.
export function aggregateDailyUptime(
  targets: TargetWithHistory[],
): Array<number | null> {
  if (targets.length === 0) {
    // eslint-disable-next-line unicorn/no-new-array
    return new Array<number | null>(UPTIME_SPARK_DAYS).fill(null)
  }
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const totalsByDay = new Map<string, { up: number; total: number }>()
  for (const t of targets) {
    if (t.IsMuted) continue
    for (const d of t.DailyHistory ?? []) {
      const slot = totalsByDay.get(d.Date) ?? { up: 0, total: 0 }
      slot.up += d.TotalChecks - d.DownChecks
      slot.total += d.TotalChecks
      totalsByDay.set(d.Date, slot)
    }
  }
  const out: Array<number | null> = []
  for (let i = UPTIME_SPARK_DAYS - 1; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(today.getDate() - i)
    const key = date.toISOString().slice(0, 10)
    const slot = totalsByDay.get(key)
    out.push(slot && slot.total > 0 ? (100 * slot.up) / slot.total : null)
  }
  return out
}

// Hourly latency, averaged across every target's hourly bucket.
// Aligned to wall-clock hours so all rows of the sparkline align,
// regardless of how many real buckets a particular target reported.
export function aggregateHourlyLatency(
  targets: TargetWithHistory[],
): Array<number | null> {
  if (targets.length === 0) {
    // eslint-disable-next-line unicorn/no-new-array
    return new Array<number | null>(UPTIME_SPARK_HOURS).fill(null)
  }
  const now = new Date()
  now.setMinutes(0, 0, 0)
  const sums = new Map<number, { sum: number; count: number }>()
  for (const t of targets) {
    if (t.IsMuted) continue
    for (const h of t.LatencyHourlyHistory ?? []) {
      if (h.AvgDurationMs == null || h.AvgDurationMs <= 0) continue
      const bucket = new Date(h.BucketStart).getTime()
      const offset = Math.round((now.getTime() - bucket) / 3_600_000)
      if (offset < 0 || offset >= UPTIME_SPARK_HOURS) continue
      const slot = sums.get(offset) ?? { sum: 0, count: 0 }
      slot.sum += h.AvgDurationMs
      slot.count += 1
      sums.set(offset, slot)
    }
  }
  const out: Array<number | null> = []
  for (let i = UPTIME_SPARK_HOURS - 1; i >= 0; i--) {
    const slot = sums.get(i)
    out.push(slot ? slot.sum / slot.count : null)
  }
  return out
}

// Daily incident counts: one bar per day = number of targets that
// had at least one down probe that day. Mirrors what an operator
// would expect to see if they scanned the daily history strip.
export function aggregateDailyIncidents(
  targets: TargetWithHistory[],
): Array<number | null> {
  if (targets.length === 0) {
    // eslint-disable-next-line unicorn/no-new-array
    return new Array<number | null>(UPTIME_SPARK_DAYS).fill(null)
  }
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const countsByDay = new Map<string, number>()
  for (const t of targets) {
    if (t.IsMuted) continue
    for (const d of t.DailyHistory ?? []) {
      if (d.DownChecks > 0) {
        countsByDay.set(d.Date, (countsByDay.get(d.Date) ?? 0) + 1)
      }
    }
  }
  const out: Array<number | null> = []
  for (let i = UPTIME_SPARK_DAYS - 1; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(today.getDate() - i)
    const key = date.toISOString().slice(0, 10)
    out.push(countsByDay.get(key) ?? 0)
  }
  return out
}

// Daily drift-detection bars. Each target has at most one
// DriftDetectedAt timestamp; we bucket by day so a wave of drift
// detections shows up as a visible spike under the card.
export function aggregateDailyDriftDetections(
  targets: TargetWithHistory[],
): Array<number | null> {
  if (targets.length === 0) {
    // eslint-disable-next-line unicorn/no-new-array
    return new Array<number | null>(UPTIME_SPARK_DAYS).fill(null)
  }
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const countsByDay = new Map<string, number>()
  for (const t of targets) {
    if (t.IsMuted) continue
    if (!t.DriftDetectedAt) continue
    const key = new Date(t.DriftDetectedAt).toISOString().slice(0, 10)
    countsByDay.set(key, (countsByDay.get(key) ?? 0) + 1)
  }
  const out: Array<number | null> = []
  for (let i = UPTIME_SPARK_DAYS - 1; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(today.getDate() - i)
    const key = date.toISOString().slice(0, 10)
    out.push(countsByDay.get(key) ?? 0)
  }
  return out
}
